import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { rows, type CustomFieldDefinition, type Employee, type EmployeeInput, type EmployeeJob } from '@hrms/shared';
import { api } from '../../../lib/api';
import { humanize, todayISO } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { useGo, useParams } from '../../../navigation/nav';
import { EmployeePicker } from '../components/EmployeePicker';
import { useOrgOptions } from '../components/orgOptions';
import { Alert, Button, Card, Checkbox, DateField, EmptyState, ErrorState, Grid, Loading, PageHeader, Screen, Select, Stack, Switch, Tabs, Text, TextField } from '../../../ui';

/** Values the API returns masked (e.g. XXXXX1234F) — only sent back when the user typed a new value. */
const SENSITIVE = ['pan', 'aadhaar', 'uan', 'esic_number', 'bank_account_number', 'bank_ifsc'] as const;

type Vals = Record<string, unknown>;
type TabKey = 'personal' | 'job' | 'statutory' | 'custom';

const fromEmployee = (e?: Employee): { emp: Vals; job: Vals; custom: Vals } => ({
  emp: e
    ? {
        emp_code: e.emp_code, first_name: e.first_name, last_name: e.last_name, email: e.email, personal_email: e.personal_email, phone: e.phone,
        date_of_birth: e.date_of_birth, gender: e.gender, marital_status: e.marital_status, blood_group: e.blood_group, address: e.address,
        date_of_joining: e.date_of_joining, confirmation_date: e.confirmation_date, status: e.status,
        pan: e.pan, aadhaar: e.aadhaar, uan: e.uan, esic_number: e.esic_number, bank_name: e.bank_name, bank_account_number: e.bank_account_number, bank_ifsc: e.bank_ifsc,
      }
    : { status: 'onboarding', date_of_joining: todayISO() },
  job: e?.current_job
    ? {
        legal_entity_id: e.current_job.legal_entity_id, location_id: e.current_job.location_id, department_id: e.current_job.department_id,
        designation_id: e.current_job.designation_id, grade_id: e.current_job.grade_id, cost_centre_id: e.current_job.cost_centre_id,
        manager_id: e.current_job.manager_id, dotted_manager_id: e.current_job.dotted_manager_id, employment_type: e.current_job.employment_type,
      }
    : { employment_type: 'full_time' },
  custom: { ...(e?.custom_fields ?? {}) },
});

function CustomFieldInput({ def, value, onChange, error }: { def: CustomFieldDefinition; value: unknown; onChange: (v: unknown) => void; error?: string }) {
  switch (def.type) {
    case 'boolean':
      return <Switch label={def.label} description={error} value={!!value} onChange={onChange} />;
    case 'select':
      return <Select label={def.label} required={def.is_required} error={error} value={value == null ? null : String(value)} options={(def.options ?? []).map((o) => ({ value: o, label: o }))} onChange={onChange} />;
    case 'date':
      return <DateField label={def.label} required={def.is_required} error={error} value={value == null ? '' : String(value)} clearable onChange={(d) => onChange(d || null)} />;
    default:
      return (
        <TextField
          label={def.label}
          required={def.is_required}
          error={error}
          keyboardType={def.type === 'number' ? 'decimal-pad' : 'default'}
          value={value == null ? '' : String(value)}
          onChangeText={(t) => onChange(def.type === 'number' ? (t === '' ? null : Number(t.replace(/[^0-9.-]/g, ''))) : t)}
        />
      );
  }
}

/** Add / edit employee (screens EmployeeNew and EmployeeEdit { id }). */
export function EmployeeFormScreen() {
  const idParam = useParams<{ id: string }>().id;
  const id = idParam ? Number(idParam) : null;
  const editing = !!id;
  const go = useGo();
  const q = useQuery({ queryKey: ['employee', id], queryFn: () => api.employees.get(id!), enabled: editing });
  const defs = useQuery({ queryKey: ['custom-fields', 'all'], queryFn: () => api.customFields.list({ per_page: 200 }) });
  const org = useOrgOptions();
  const [tab, setTab] = useState<TabKey>('personal');
  const init = fromEmployee();
  const [emp, setEmp] = useState<Vals>(init.emp);
  const [job, setJob] = useState<Vals>(init.job);
  const [custom, setCustom] = useState<Vals>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [createUser, setCreateUser] = useState(true);
  const [errs, setErrs] = useState<Record<string, string>>({});

  useEffect(() => {
    if (q.data) {
      const v = fromEmployee(q.data);
      setEmp(v.emp);
      setJob(v.job);
      setCustom(v.custom);
      setTouched(new Set());
    }
  }, [q.data]);

  const defList = useMemo(() => [...rows(defs.data)].sort((a, b) => a.sort_order - b.sort_order), [defs.data]);

  const save = useMut(
    () => {
      const e: Vals = { ...emp };
      // masked sensitive values are only sent if the user edited them
      if (editing) SENSITIVE.forEach((k) => !touched.has(k) && delete e[k]);
      for (const k of Object.keys(e)) if (e[k] === '') e[k] = null;
      const body: EmployeeInput = { ...(e as EmployeeInput), job: job as Partial<EmployeeJob>, custom_fields: custom };
      if (!editing) body.create_user = createUser;
      return editing ? api.employees.update(id!, body) : api.employees.create(body);
    },
    {
      invalidate: [['employees'], ['employee'], ['dashboard']],
      success: editing ? 'Employee updated' : 'Employee created',
      onSuccess: (r) => go('EmployeeDetail', { id: r.id }),
      onError: (e) => {
        const fe = fieldErrors(e);
        setErrs(fe);
        const keys = Object.keys(fe);
        if (keys.some((k) => k.startsWith('job.'))) setTab('job');
        else if (keys.some((k) => k.startsWith('custom_fields.'))) setTab('custom');
        else if (keys.some((k) => (SENSITIVE as readonly string[]).includes(k) || k === 'bank_name')) setTab('statutory');
        else if (keys.length) setTab('personal');
      },
    },
  );

  const tabErr = useMemo(() => {
    const keys = Object.keys(errs);
    const j = keys.filter((k) => k.startsWith('job.')).length;
    const c = keys.filter((k) => k.startsWith('custom_fields.')).length;
    const s = keys.filter((k) => (SENSITIVE as readonly string[]).includes(k) || k === 'bank_name').length;
    return { job: j, custom: c, statutory: s, personal: keys.length - j - c - s };
  }, [errs]);

  const validateAndSave = () => {
    const local: Record<string, string> = {};
    if (!String(emp.first_name ?? '').trim()) local.first_name = 'First name is required';
    if (!String(emp.email ?? '').trim()) local.email = 'Work email is required';
    if (!emp.date_of_joining) local.date_of_joining = 'Date of joining is required';
    defList.forEach((d) => {
      if (d.is_required && d.type !== 'boolean' && (custom[d.key] == null || custom[d.key] === '')) local[`custom_fields.${d.key}`] = `${d.label} is required`;
    });
    if (Object.keys(local).length) {
      setErrs(local);
      setTab(Object.keys(local).some((k) => !k.startsWith('custom_fields.')) ? 'personal' : 'custom');
      return;
    }
    setErrs({});
    save.mutate(undefined);
  };

  if (editing && q.isLoading) return <Loading />;
  if (editing && q.isError)
    return (
      <Screen>
        <PageHeader title="Edit employee" />
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </Screen>
    );

  const txt = (k: string, label: string, o: { keyboard?: 'email-address' | 'phone-pad' | 'number-pad'; required?: boolean; placeholder?: string; caps?: 'characters' | 'none'; multiline?: boolean } = {}) => (
    <TextField
      key={k}
      label={label}
      required={o.required}
      error={errs[k]}
      placeholder={o.placeholder}
      keyboardType={o.keyboard}
      autoCapitalize={o.caps ?? (o.keyboard === 'email-address' ? 'none' : undefined)}
      multiline={o.multiline}
      value={emp[k] == null ? '' : String(emp[k])}
      onChangeText={(v) => {
        setEmp((s) => ({ ...s, [k]: v }));
        setTouched((t) => new Set(t).add(k));
      }}
      testID={`emp-${k}`}
    />
  );
  const date = (k: string, label: string, required?: boolean) => <DateField key={k} label={label} required={required} error={errs[k]} value={emp[k] as string | null} clearable={!required} onChange={(d) => setEmp((s) => ({ ...s, [k]: d || null }))} />;
  const sel = (k: string, label: string, options: { value: string; label: string }[]) => <Select key={k} label={label} error={errs[k]} value={emp[k] as string | null} options={options} onChange={(v) => setEmp((s) => ({ ...s, [k]: v }))} />;
  const jobSel = (k: string, label: string, options: { value: number; label: string }[]) => (
    <Select<number | ''> key={k} label={label} error={errs[`job.${k}`]} placeholder="—" value={(job[k] as number | null) ?? ''} options={[{ value: '', label: '—' }, ...options]} onChange={(v) => setJob((s) => ({ ...s, [k]: v === '' ? null : v }))} />
  );
  const label = (s: string, n: number) => (n ? `${s} (${n}!)` : s);

  return (
    <Screen testID="employee-form">
      <PageHeader
        title={editing ? `Edit ${q.data?.full_name ?? 'employee'}` : 'Add employee'}
        actions={
          <>
            <Button title="Cancel" onPress={() => (editing ? go('EmployeeDetail', { id }) : go('EmployeeList'))} />
            <Button variant="primary" title={editing ? 'Save changes' : 'Create employee'} loading={save.isPending} onPress={validateAndSave} testID="emp-save" />
          </>
        }
      />
      {Object.keys(errs).length > 0 && <Alert tone="danger">Please fix the highlighted fields.</Alert>}
      <Tabs<TabKey>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'personal', label: label('Personal', tabErr.personal) },
          { key: 'job', label: label('Job', tabErr.job) },
          { key: 'statutory', label: label('Statutory & bank', tabErr.statutory) },
          { key: 'custom', label: label('Custom fields', tabErr.custom), count: defList.length || undefined },
        ]}
      />
      <Card>
        {tab === 'personal' && (
          <Stack gap={14}>
            <Grid min={220} gap={14}>
              {txt('emp_code', 'Employee code', { placeholder: editing ? undefined : 'Auto if blank', caps: 'characters' })}
              {txt('first_name', 'First name', { required: true })}
              {txt('last_name', 'Last name')}
              {txt('email', 'Work email', { keyboard: 'email-address', required: true })}
              {txt('personal_email', 'Personal email', { keyboard: 'email-address' })}
              {txt('phone', 'Mobile', { keyboard: 'phone-pad' })}
              {date('date_of_birth', 'Date of birth')}
              {sel('gender', 'Gender', [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }])}
              {sel('marital_status', 'Marital status', ['single', 'married', 'divorced', 'widowed'].map((v) => ({ value: v, label: humanize(v) })))}
              {txt('blood_group', 'Blood group', { caps: 'characters' })}
              {date('date_of_joining', 'Date of joining', true)}
              {date('confirmation_date', 'Confirmation date')}
              {sel('status', 'Status', ['onboarding', 'probation', 'active', 'notice', 'exited'].map((v) => ({ value: v, label: humanize(v) })))}
            </Grid>
            {txt('address', 'Address', { multiline: true })}
            {!editing && <Checkbox label="Create a login for this employee and send an invite" value={createUser} onChange={setCreateUser} />}
          </Stack>
        )}
        {tab === 'job' && (
          <Stack gap={14}>
            <Grid min={220} gap={14}>
              {jobSel('legal_entity_id', 'Legal entity', org.legalEntities)}
              {jobSel('location_id', 'Location', org.locations)}
              {jobSel('department_id', 'Department', org.departments)}
              {jobSel('designation_id', 'Designation', org.designations)}
              {jobSel('grade_id', 'Grade', org.grades)}
              {jobSel('cost_centre_id', 'Cost centre', org.costCentres)}
              <EmployeePicker label="Reporting manager" error={errs['job.manager_id']} value={job.manager_id as number | null} onChange={(v) => setJob((s) => ({ ...s, manager_id: v }))} />
              <EmployeePicker label="Dotted-line manager" error={errs['job.dotted_manager_id']} value={job.dotted_manager_id as number | null} onChange={(v) => setJob((s) => ({ ...s, dotted_manager_id: v }))} />
              <Select
                label="Employment type"
                error={errs['job.employment_type']}
                value={(job.employment_type as string) ?? 'full_time'}
                onChange={(v) => setJob((s) => ({ ...s, employment_type: v }))}
                options={[{ value: 'full_time', label: 'Full time' }, { value: 'part_time', label: 'Part time' }, { value: 'contract', label: 'Contract' }, { value: 'intern', label: 'Intern' }]}
              />
            </Grid>
            {editing && <Text variant="small">Changing job details here corrects the current record. Use Transfer / Promote on the employee page to create a dated job-history entry.</Text>}
          </Stack>
        )}
        {tab === 'statutory' && (
          <Stack gap={14}>
            <Grid min={220} gap={14}>
              {txt('pan', 'PAN', { placeholder: 'ABCDE1234F', caps: 'characters' })}
              {txt('aadhaar', 'Aadhaar', { keyboard: 'number-pad' })}
              {txt('uan', 'UAN', { keyboard: 'number-pad' })}
              {txt('esic_number', 'ESIC number', { keyboard: 'number-pad' })}
              {txt('bank_name', 'Bank name')}
              {txt('bank_account_number', 'Account number', { keyboard: 'number-pad' })}
              {txt('bank_ifsc', 'IFSC', { caps: 'characters' })}
            </Grid>
            {editing && <Text variant="small">Masked values are kept unless you type a new value.</Text>}
          </Stack>
        )}
        {tab === 'custom' &&
          (defs.isLoading ? (
            <Loading />
          ) : defList.length === 0 ? (
            <EmptyState icon="sliders" title="No custom fields defined" description="Configure them under HR → Custom fields." />
          ) : (
            <Grid min={220} gap={14}>
              {defList.map((d) => (
                <View key={d.id}>
                  <CustomFieldInput def={d} value={custom[d.key]} error={errs[`custom_fields.${d.key}`]} onChange={(v) => setCustom((s) => ({ ...s, [d.key]: v }))} />
                </View>
              ))}
            </Grid>
          ))}
      </Card>
    </Screen>
  );
}
